import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, CheckSquare, Square, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_META = {
  high:   { label: 'High',   badge: 'bg-red-100 text-red-800 border-red-200' },
  medium: { label: 'Medium', badge: 'bg-amber-100 text-amber-800 border-amber-200' },
  low:    { label: 'Low',    badge: 'bg-gray-100 text-gray-700 border-gray-200' },
};

export const TodoList = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [newPriority, setNewPriority] = useState('medium');
  const [adding, setAdding] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  const authHeaders = useMemo(() => ({
    Authorization: `Basic ${btoa(':' + password)}`,
    'Content-Type': 'application/json',
  }), [password]);

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchTodos();
  }, []);

  const fetchTodos = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/todos`, { headers: authHeaders });
      if (res.status === 401) { navigate('/admin/login'); return; }
      setTodos(await res.json());
    } catch (err) {
      logger.error('Fetch todos:', err);
      toast.error('Failed to load to-dos');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const title = newTitle.trim();
    if (!title) return;
    setAdding(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/todos`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ title, priority: newPriority }),
      });
      if (!res.ok) throw new Error('add failed');
      const created = await res.json();
      setTodos((prev) => [created, ...prev]);
      setNewTitle('');
      setNewPriority('medium');
    } catch (err) {
      logger.error('Add todo:', err);
      toast.error('Failed to add to-do');
    } finally {
      setAdding(false);
    }
  };

  const toggleCompleted = async (todo) => {
    // Optimistic UI
    const nextCompleted = !todo.completed;
    setTodos((prev) => prev.map((t) => (t.id === todo.id ? { ...t, completed: nextCompleted } : t)));
    try {
      const res = await fetch(`${API_URL}/api/admin/todos/${todo.id}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({ completed: nextCompleted }),
      });
      if (!res.ok) throw new Error('toggle failed');
      const updated = await res.json();
      setTodos((prev) => prev.map((t) => (t.id === todo.id ? updated : t)));
    } catch (err) {
      logger.error('Toggle todo:', err);
      toast.error('Failed to update');
      // Rollback
      setTodos((prev) => prev.map((t) => (t.id === todo.id ? todo : t)));
    }
  };

  const handleDelete = async (todo) => {
    if (!window.confirm(`Delete "${todo.title}"?`)) return;
    setTodos((prev) => prev.filter((t) => t.id !== todo.id));
    try {
      const res = await fetch(`${API_URL}/api/admin/todos/${todo.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('delete failed');
    } catch (err) {
      logger.error('Delete todo:', err);
      toast.error('Failed to delete');
      fetchTodos(); // resync
    }
  };

  const handleClearCompleted = async () => {
    const completedCount = todos.filter((t) => t.completed).length;
    if (completedCount === 0) return;
    if (!window.confirm(`Delete all ${completedCount} completed to-do${completedCount === 1 ? '' : 's'}?`)) return;
    try {
      await fetch(`${API_URL}/api/admin/todos/completed/all`, { method: 'DELETE', headers: authHeaders });
      setTodos((prev) => prev.filter((t) => !t.completed));
      toast.success('Completed to-dos cleared');
    } catch (err) {
      logger.error('Clear completed:', err);
      toast.error('Failed to clear completed');
    }
  };

  const pending = todos.filter((t) => !t.completed);
  const completed = todos.filter((t) => t.completed);

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-5">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="text-sm text-foreground/70 hover:text-foreground inline-flex items-center gap-2 mb-3"
            data-testid="todos-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground inline-flex items-center gap-2">
                <ListChecks className="w-7 h-7 text-primary" />
                To-Do List
              </h1>
              <p className="text-sm text-foreground/70 mt-1">
                {pending.length} open · {completed.length} completed
              </p>
            </div>
            {completed.length > 0 && (
              <button
                onClick={handleClearCompleted}
                data-testid="clear-completed-btn"
                className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 inline-flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" /> Clear completed
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-6 max-w-3xl">
        {/* Add form */}
        <form onSubmit={handleAdd} className="bg-white rounded-xl border border-border p-4 mb-6 flex flex-col sm:flex-row gap-3" data-testid="add-todo-form">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Add a new task…"
            maxLength={300}
            data-testid="new-todo-input"
            className="flex-1 px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <select
            value={newPriority}
            onChange={(e) => setNewPriority(e.target.value)}
            data-testid="new-todo-priority"
            className="px-3 py-2 border border-border rounded-lg text-sm"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
          <Button type="submit" disabled={adding || !newTitle.trim()} data-testid="add-todo-btn" className="inline-flex items-center gap-2">
            <Plus className="w-4 h-4" />
            {adding ? 'Adding…' : 'Add'}
          </Button>
        </form>

        {loading ? (
          <div className="text-center text-foreground/60 py-10">Loading…</div>
        ) : (
          <>
            {/* Pending */}
            <div className="bg-white rounded-xl border border-border overflow-hidden mb-6">
              <div className="px-5 py-3 border-b border-border">
                <h2 className="font-semibold text-foreground">Open ({pending.length})</h2>
              </div>
              {pending.length === 0 ? (
                <p className="p-8 text-center text-sm text-foreground/50">
                  Nothing on the list — you&apos;re all caught up. ✨
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {pending.map((t) => (
                    <TodoRow key={t.id} todo={t} onToggle={toggleCompleted} onDelete={handleDelete} />
                  ))}
                </ul>
              )}
            </div>

            {/* Completed (collapsible) */}
            {completed.length > 0 && (
              <div className="bg-white rounded-xl border border-border overflow-hidden">
                <button
                  onClick={() => setShowCompleted((s) => !s)}
                  className="w-full px-5 py-3 border-b border-border text-left hover:bg-gray-50 flex items-center justify-between"
                  data-testid="toggle-completed-section"
                >
                  <h2 className="font-semibold text-foreground/70">Completed ({completed.length})</h2>
                  <span className="text-xs text-foreground/50">{showCompleted ? 'Hide' : 'Show'}</span>
                </button>
                {showCompleted && (
                  <ul className="divide-y divide-border">
                    {completed.map((t) => (
                      <TodoRow key={t.id} todo={t} onToggle={toggleCompleted} onDelete={handleDelete} />
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const TodoRow = ({ todo, onToggle, onDelete }) => {
  const p = PRIORITY_META[todo.priority] || PRIORITY_META.medium;
  return (
    <li className="flex items-center gap-3 px-5 py-3 group" data-testid={`todo-row-${todo.id}`}>
      <button
        type="button"
        onClick={() => onToggle(todo)}
        data-testid={`todo-toggle-${todo.id}`}
        aria-label={todo.completed ? 'Mark as not done' : 'Mark as done'}
        className="flex-shrink-0 text-foreground/50 hover:text-primary transition-colors"
      >
        {todo.completed ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${todo.completed ? 'line-through text-foreground/40' : 'text-foreground'}`}>
          {todo.title}
        </p>
      </div>
      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${p.badge} flex-shrink-0`}>
        {p.label}
      </span>
      <button
        type="button"
        onClick={() => onDelete(todo)}
        data-testid={`todo-delete-${todo.id}`}
        aria-label="Delete"
        className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-foreground/40 hover:text-red-600 transition-all flex-shrink-0"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
};

export default TodoList;
